/**
 * HbarPortfolioWidget — V2.J.3
 *
 * Contextual portfolio alert that surfaces a recent HBAR treasury
 * release ONLY when the current user holds HBAR. Designed to live
 * inside PortfolioDashboard, just above the holdings table.
 *
 * Why a dedicated component :
 *  - The page-wide /hedera-monitor banner is too broad — most users
 *    don't watch a Hedera wallet.
 *  - HBAR holders care directly about supply dilution events, so we
 *    push the signal where they'll see it (their portfolio).
 *
 * Renders nothing when :
 *  - The user holds 0 HBAR.
 *  - No release has been detected in the last `maxAgeDays` days.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { useHbarReleaseEvents } from '../../hooks/useHbarReleaseEvents';
import type { Token } from '../../types';

interface HbarPortfolioWidgetProps {
  tokens: Token[];
  /** Optional callback to navigate to the Hedera monitor page. If
   *  omitted, the widget falls back to a hard <a href> to
   *  /hedera-monitor. */
  onNavigate?: (page: string) => void;
  /** Max age (days) of the latest release to keep the widget
   *  visible. Default 7. */
  maxAgeDays?: number;
}

const HBAR_TOKEN_IDS = new Set(['hedera-hashgraph', 'hbar']);
const HBAR_SYMBOLS = new Set(['HBAR', 'ℏ']);

export function HbarPortfolioWidget({
  tokens,
  onNavigate,
  maxAgeDays = 7,
}: HbarPortfolioWidgetProps) {
  const { t, i18n } = useTranslation();
  const { latest } = useHbarReleaseEvents({ limit: 1 });

  // Find the user's HBAR token and total quantity.
  const userHbar = useMemo(() => {
    const token = tokens.find((tok) => {
      const id = (tok.id || '').toLowerCase();
      const display = (tok.displayName || '').toUpperCase();
      return HBAR_TOKEN_IDS.has(id) || HBAR_SYMBOLS.has(display);
    });
    if (!token) return null;
    const totalQty = (token.positions || []).reduce(
      (sum, pos) => sum + (Number(pos.quantity) || 0),
      0,
    );
    if (totalQty <= 0) return null;
    return { token, totalQty };
  }, [tokens]);

  const isRecent = useMemo(() => {
    if (!latest) return false;
    const ageMs = Date.now() - new Date(latest.detected_at).getTime();
    return ageMs <= maxAgeDays * 24 * 3600 * 1000;
  }, [latest, maxAgeDays]);

  if (!userHbar || !isRecent || !latest) return null;

  const locale = i18n?.language || 'fr';
  const fmt = (n: number) =>
    n.toLocaleString(locale === 'fr' ? 'fr-FR' : locale, { maximumFractionDigits: 0 });

  // Share of circulating supply represented by this release.
  const supplyImpactPct = latest.total_hbar
    ? (latest.delta_hbar / latest.new_released_hbar) * 100
    : 0;

  const handleNavigate = (e: React.MouseEvent) => {
    if (onNavigate) {
      e.preventDefault();
      onNavigate('hedera-monitor');
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        background:
          'linear-gradient(135deg, rgba(0, 194, 255, 0.10), rgba(130, 89, 239, 0.08))',
        border: '1px solid rgba(0, 194, 255, 0.30)',
        borderRadius: '12px',
        padding: '14px 16px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap',
      }}
    >
      <div
        aria-hidden
        style={{
          width: '38px',
          height: '38px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #000000, #00C2FF)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          fontWeight: 700,
          color: 'white',
          flexShrink: 0,
        }}
      >
        ℏ
      </div>

      <div style={{ flex: 1, minWidth: '220px' }}>
        <div
          style={{
            fontSize: '12px',
            fontWeight: 700,
            color: '#00C2FF',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '2px',
          }}
        >
          {t('hedera.portfolioWidget.title', 'Libération HBAR détectée')}
        </div>
        <div style={{ fontSize: '13px', color: 'var(--text)', lineHeight: 1.5 }}>
          {t(
            'hedera.portfolioWidget.body',
            'Vous détenez {{user}} HBAR. La trésorerie Hedera vient de libérer {{delta}} HBAR (~{{pct}} % de la supply en circulation).',
            {
              user: fmt(userHbar.totalQty),
              delta: fmt(latest.delta_hbar),
              pct: supplyImpactPct.toFixed(2),
            },
          )}
        </div>
      </div>

      <a
        href="/hedera-monitor"
        onClick={handleNavigate}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 12px',
          background: 'rgba(0, 194, 255, 0.15)',
          color: '#00C2FF',
          border: '1px solid rgba(0, 194, 255, 0.35)',
          borderRadius: '8px',
          fontSize: '12px',
          fontWeight: 600,
          textDecoration: 'none',
          whiteSpace: 'nowrap',
          cursor: 'pointer',
        }}
      >
        <Icons.ExternalLink size={12} />
        {t('hedera.portfolioWidget.viewMonitor', 'Voir le détail')}
      </a>
    </div>
  );
}
